import { EventEmitter, inject, Injectable } from '@angular/core';

import { BaasAppState, BarGvkCompatibilityMode, BarGvkManager, BarGvkSettings } from '@lvflanders/bar';
import { _BaasWindow } from '@lvflanders/bar-ng';

import { BAR_LAYOUT_CONSTANTS } from './bar-layout.constants';
import { BarMenuState } from './bar-menu-state.type';
import { BAAS_LAYOUT_GVK } from './layout-tokens';

// NOTE do not expose this, this is a man-in-the-middle class
/** Internal service for gvk stuff with the layout.*/
@Injectable({ providedIn: 'root' })
export class BarLayoutInternalGvkService {
    readonly #window = inject(_BaasWindow);
    readonly #gvkManager = inject(BarGvkManager, { optional: true });
    readonly #state = inject(BaasAppState, { optional: true });
    readonly #gvkEnabled = inject<boolean>(BAAS_LAYOUT_GVK);
    // event emitter invoked when the layout needs to set the menu state
    readonly barMenuStateChanged = new EventEmitter<BarMenuState>();
    // invoked when the layout needs to set the compact modus
    readonly compactChanged = new EventEmitter<boolean>();
    readonly #gvkSettings: BarGvkSettings = {
        // storage under webcore appcode because it should be compatible with Legacy MVC apps
        appCode: 'BWCWUI',
        // run in GVK version 1 which is compatible with MVC applications
        compatibilityMode: BarGvkCompatibilityMode.Version_1
    };
    // flag used to determine whether the page state can be changed by the behavior subject subcribes in the layout service
    #allowPersistPageState = true;
    #barMenuState: BarMenuState;
    #barPageState: string;
    #disabled = false;

    /** Whether disabled */
    get disabled(): boolean {
        // user storage must be injected
        return this.#gvkManager == null || !this.#state || !this.#state.basiscontext || this.#gvkEnabled === false || this.#disabled;
    }

    /** Invoked before user interface is loaded */
    async preInit(): Promise<void> {
        if (!this.disabled) {
            const disabled = await this.#gvkManager.checkDisabled(this.#gvkSettings.appCode);

            this.#disabled = disabled;

            if (!disabled) {
                const menuState = await this.#gvkManager.getItem<string>(BAR_LAYOUT_CONSTANTS.GVK_KEYS.MENU_STATE, this.#gvkSettings);
                const pageState = await this.#gvkManager.getItem<string>(BAR_LAYOUT_CONSTANTS.GVK_KEYS.PAGE_STATE, this.#gvkSettings);
                // combine observables
                // set local state
                this.#barMenuState =
                    menuState && menuState['bar-layout-menu'] && menuState['bar-layout-menu']['OTHER']
                        ? menuState['bar-layout-menu']['OTHER']['bar-menu-state'] || 'default'
                        : 'default';
                this.#barPageState =
                    pageState && pageState['html'] && pageState['html']['OTHER'] ? pageState['html']['OTHER']['barPageState'] || 'default' : 'default';
            }
        }
    }

    /** Does the initialization. */
    init(): void {
        if (!this.disabled) {
            // CSS will apply itself for the menu based on next dom value
            document.documentElement.setAttribute('data-bar-menu-state', this.#barMenuState);
            this.barMenuStateChanged.emit(this.#barMenuState);

            document.documentElement.setAttribute('data-bar-page-layout', this.#barPageState);
            this.#allowPersistPageState = false;
            // notify the layout to set compact and touch modus (without saving to gvk)
            this.compactChanged.emit(this.#barPageState === 'compact');
            // timeout as effects run asynchronously (in BaasShell) we have an effect on this which triggers gvk persistPageState
            this.#window.setTimeoutOutsideAngular(() => (this.#allowPersistPageState = true));
        }
    }

    /**
     * Persist the chosen menu state into the user storage.
     * @param barMenuState State of the menu.
     */
    persistMenuState(barMenuState: string): void {
        if (!this.disabled) {
            // use legacy GVK format for compatibility with ASP.NET MVC applications
            const value = {
                _gvk_timeStamp: new Date().getTime(),
                _gvk_setName: BAR_LAYOUT_CONSTANTS.GVK_KEYS.MENU_STATE,
                'bar-layout-menu': {
                    OTHER: {
                        'bar-menu-state': barMenuState
                    }
                }
            };
            // save in the user storage
            this.#gvkManager.setItem(BAR_LAYOUT_CONSTANTS.GVK_KEYS.MENU_STATE, value, this.#gvkSettings);
        }
    }

    // Set new page state and save to gvk
    /**
     * Persists the chosen page state into the user storage.
     * @param barPageState State of the page.
     */
    persistPageState(barPageState: string): void {
        if (!this.disabled && this.#allowPersistPageState) {
            document.documentElement.setAttribute('data-bar-page-layout', barPageState);
            // use legacy GVK format for compatibility with ASP.NET MVC applications
            const value = {
                _gvk_timeStamp: new Date().getTime(),
                _gvk_setName: BAR_LAYOUT_CONSTANTS.GVK_KEYS.PAGE_STATE,
                html: {
                    OTHER: { barPageState: barPageState }
                }
            };
            this.#gvkManager.setItem(BAR_LAYOUT_CONSTANTS.GVK_KEYS.PAGE_STATE, value, this.#gvkSettings);
        }
    }
}
